import React from 'react';
import cx from 'classnames';

import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import SectionHeader from '@marketing/components/section_header';
import FAQSection from '@marketing/sections/faq';
import { OfferProps } from '@marketing/types';

import TestimonialsSection from './sections/testimonials';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import HeroSection from '../sections/hero';
import OffersSection from '../sections/offers';
import StartYourPrepSection from '../sections/start_your_prep';

import AITutorIcon from '@marketing/sections/value_props/assets/green/banner_check_icon.svg';
import PracticeQuestionsIcon from '@marketing/sections/value_props/assets/green/practice_questions_icon.svg';
import PracticeTestsIcon from '@marketing/sections/value_props/assets/green/practice_tests_icon.svg';
import ScorePredictorIcon from '@marketing/sections/value_props/assets/green/score_gauge_icon.svg';
import StudySchedulesIcon from '@marketing/sections/value_props/assets/green/timely_guidance_icon.svg';
import VideoLessonsIcon from '@marketing/sections/value_props/assets/green/video_lessons_icon.svg';

import BannerCheckIcon from '../sections/competitor_comparison/assets/banner_check_icon.svg';
import BubbleQuestionIcon from '../sections/competitor_comparison/assets/bubble_question_icon.svg';
import ChecklistIcon from '../sections/competitor_comparison/assets/checklist_icon.svg';
import CircleRefreshIcon from '../sections/competitor_comparison/assets/circle_refresh_icon.svg';
import DollarSignIcon from '../sections/competitor_comparison/assets/dollar_sign_icon.svg';
import FireworkIcon from '../sections/competitor_comparison/assets/firework_icon.svg';
import FlashcardsIcon from '../sections/competitor_comparison/assets/flashcards_icon.svg';
import HighFiveIcon from '../sections/competitor_comparison/assets/high_five_icon.svg';
import HourglassIcon from '../sections/competitor_comparison/assets/hourglass_icon.svg';
import MobileDeviceIcon from '../sections/competitor_comparison/assets/mobile_device_icon.svg';
import NotebookIcon from '../sections/competitor_comparison/assets/notebook_icon.svg';
import ScoreGaugeIcon from '../sections/competitor_comparison/assets/score_gauge_icon.svg';
import StarIcon from '../sections/competitor_comparison/assets/star_icon.svg';
import VideoPlayerIcon from '../sections/competitor_comparison/assets/video_player_icon.svg';

import styles from '../style.module.scss';
import marketingStyles from '@marketing/style.module.scss';

interface Props extends PageProps {
  hideSignUp: boolean;
  isLoggedIn: boolean;
  offers: OfferProps[];
}

export default (props: Props) => {
  const premium6MonthsOffer = props.offers.find((offer) => offer.slug === 'premium-6-month');
  const premiumOffer = props.offers.find((offer) => offer.slug === 'premium-1-month');
  const trialOffer = props.offers.find((offer) => offer.slug === 'basic');

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.greenGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />
        <div className={styles.greenEllipse} />
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 30 }}
        dashedBorder
        examName="TOEFL"
        navigationItems={props.navigationItems}
        notice={props.notice}
      >

        <HeroSection
          examName="TOEFL"
          header={<>TOEFL<span className={styles.registeredMark}>®</span> Score Improvement Made Easy</>}
          subHeader="7-Day Money Back Guarantee: Email us if you want to cancel for any reason. No strings attached."
          valueProps={[
            {
              description: "4 official, full-length practice tests directly from ETS",
              icon: PracticeTestsIcon,
              name: "Practice Tests"
            },
            {
              description: "All TOEFL® questions are created by ETS and used under license",
              icon: PracticeQuestionsIcon,
              name: "1300+ Official  Questions"
            },
            {
              description: "Prepare for every section, on any device and 100% online",
              icon: VideoLessonsIcon,
              name: "Expert Video Lessons"
            },
            {
              description: "Get instant score reports showing where you stand and how to improve",
              icon: StudySchedulesIcon,
              name: "Graded Writing/Speaking"
            },
            {
              description: "Never get stuck with an AI tutor to explain any question or concept",
              icon: AITutorIcon,
              name: "AI Tutor"
            },
            {
              description: "Be confident when you're ready with our score predictor tool",
              icon: ScorePredictorIcon,
              name: "Accurate Score Predictor"
            }
          ]}
        />

        <OffersSection
          primaryOffer={{
            header: (
              <>
                <span className="text-bold text-white">Premium</span>
                {' '}<span style={{ color: '#8E70B2' }}>·</span>{' '}
                <span className="text-bold text-white">6 months</span>
              </>
            ),
            highlight: <><small><strong>Best option</strong> for most students</small></>,
            offer: premium6MonthsOffer,
            subHeader: 'Save $625 by purchasing 6 months at once',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <>Built entirely from <strong>authentic TOEFL® questions</strong> from ETS</>,
                    tooltip: "All TOEFL® practice questions are<br />authentic questions created by ETS and used<br />under license."
                  },
                  {
                    content: <><strong>100+</strong> video lessons</>,
                    tooltip: 'Lessons cover every concept tests on the TOEFL.'
                  },
                  {
                    content: <>Over <strong>1300</strong> practice questions</>,
                    tooltip: 'Every question has an in-depth video or<br />text explanation.'
                  },
                  {
                    content: <strong>4 full-length, official practice tests</strong>,
                    tooltip: 'Official, full-length tests directly from ETS.'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <>Updated for the <strong>2026 TOEFL</strong></>,
                    tooltip: 'All new lessons on how to approach<br />the updated exam.'
                  },
                  {
                    content: <>Unlimited <strong>writing and speaking grading</strong></>,
                    tooltip: 'Get an instant score and feedback on<br />how to improve.'
                  },
                  {
                    content: <><strong>6 months</strong> of access</>,
                    tooltip: 'Full access for 6 months'
                  },
                  {
                    content: <><strong>7-day</strong> refund guarantee</>,
                    tooltip: 'If you are unhappy for any reason, email us<br /> within 7 days of purchasing, and we will give<br />you a full refund.'
                  },
                  {
                    content: <><strong>+4 point</strong> score guarantee</>,
                    tooltip: "If you've taken the test before and meet our<br />requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund."
                  },
                  {
                    content: 'Ask an expert',
                    tooltip: 'Quick email and chat support for when you<br />get stuck or just want some extra help'
                  },
                  {
                    content: 'Pause your plan',
                    tooltip: "Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan's end date will be<br />extended by the paused duration."
                  }
                ],
                header: 'Features'
              }
            ]
          }}
          secondaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#9BBCAF' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">1 month</span>
              </>
            ),
            offer: premiumOffer,
            subHeader: 'Great option for students with limited study time',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <>Built entirely from <strong>authentic TOEFL® questions</strong> from ETS</>,
                    tooltip: "All TOEFL® practice questions are<br />authentic questions created by ETS and used<br />under license."
                  },
                  {
                    content: <><strong>100+</strong> video lessons</>,
                    tooltip: 'Lessons cover every concept tests on the TOEFL.'
                  },
                  {
                    content: <>Over <strong>1300</strong> practice questions</>,
                    tooltip: 'Every question has an in-depth video or<br />text explanation.'
                  },
                  {
                    content: <strong>4 full-length, official practice tests</strong>,
                    tooltip: 'Official, full-length tests directly from ETS.'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <>Updated for the <strong>2026 TOEFL</strong></>,
                    tooltip: 'All new lessons on how to approach<br />the updated exam.'
                  },
                  {
                    content: <>Unlimited <strong>writing and speaking grading</strong></>,
                    tooltip: 'Get an instant score and feedback on<br />how to improve.'
                  },
                  {
                    content: <><strong>1 month</strong> of access</>,
                    tooltip: 'Full access for 1 month'
                  },
                  {
                    content: <><strong>7-day</strong> refund guarantee</>,
                    tooltip: 'If you are unhappy for any reason, email us<br /> within 7 days of purchasing, and we will give<br />you a full refund.'
                  },
                  {
                    content: <><strong>+4 point</strong> score guarantee</>,
                    tooltip: "If you've taken the test before and meet our<br />requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund."
                  },
                  {
                    content: 'Ask an expert',
                    tooltip: 'Quick email and chat support for when you<br />get stuck or just want some extra help'
                  },
                  {
                    content: 'Pause your plan',
                    tooltip: "Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan's end date will be<br />extended by the paused duration."
                  }
                ],
                header: 'Features'
              }
            ]
          }}
          tertiaryOffer={null}
          trialOffer={{
            header: 'Try Magoosh for 7 days with no commitment',
            offer: trialOffer,
            subHeader: 'Sign up for a free trial and get to know some of our lesson videos and practice questions.'
          }}
        />

        <CompetitorComparisonSection
          companyFeatureOfferings={[
            {
              name: 'Magoosh',
              'Price': formatPrice(premium6MonthsOffer.fullPriceInCents, 'usd', true),
              'Length of Online Access': '6 months',
              'Official ETS Questions': 'Included',
              'AI Tutor': 'Included',
              'Practice Questions and Exams': 'Included',
              'Unlimited Writing Grading': 'Included',
              'Unlimited Speaking Grading': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'No Questions Asked Money-Back Guarantee': 'Included',
              'Score-Improvement Guarantee': '+4 Point Increase Guarantee',
              'Flashcards Included': 'Included',
              'Score Predictor': 'Included'
            },
            {
              name: 'BestMyTest',
              'Price': '$209',
              'Length of Online Access': '6 months',
              'AI Tutor': 'Included',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Score-Improvement Guarantee': 'Included',
              'Flashcards Included': 'Included',
              'Score Predictor': 'Included'
            },
            {
              name: 'TST Prp',
              'Price': '$197',
              'Length of Online Access': '12 months',
              'Practice Questions and Exams': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Flashcards Included': 'Included',
              'No Questions Asked Money-Back Guarantee': 'Included',
              'Score-Improvement Guarantee': 'Included',
            },
            {
              name: 'Notefull',
              'Price': '$120',
              'Length of Online Access': 'Lifetime',
              'Free Trial': 'Included',
              'Video Lessons': 'Included'
            }
          ]}
          features={[
            { icon: DollarSignIcon, name: 'Price' },
            { icon: HourglassIcon, name: 'Length of Online Access' },
            { icon: BannerCheckIcon, name: 'Official ETS Questions' },
            { icon: StarIcon, name: 'AI Tutor' },
            { icon: BubbleQuestionIcon, name: 'Practice Questions and Exams' },
            { icon: NotebookIcon, name: 'Unlimited Writing Grading' },
            { icon: ChecklistIcon, name: 'Unlimited Speaking Grading' },
            { icon: MobileDeviceIcon, name: 'Mobile App' },
            { icon: FireworkIcon, name: 'Free Trial' },
            { icon: VideoPlayerIcon, name: 'Video Lessons' },
            { icon: CircleRefreshIcon, name: 'No Questions Asked Money-Back Guarantee' },
            { icon: HighFiveIcon, name: 'Score-Improvement Guarantee' },
            { icon: FlashcardsIcon, name: 'Flashcards Included' },
            { icon: ScoreGaugeIcon, name: 'Score Predictor' }
          ]}
          registrationUrl={premiumOffer.registrationUrl}
        />

        <TestimonialsSection />

        <FAQSection
          entries={[
             {
              answer: (
                <>
                  <p>Yes! The <a href="/toefl-2026-changes">TOEFL changed on January 21, 2026</a>, and our content is fully updated for the new test format. All Magoosh lessons are aligned with the 2026 TOEFL, and our self-paced course includes over 1,300 TOEFL® practice questions used under license from ETS, the makers of the test, so you know you're studying exactly what you'll see on test day.</p>
                </>
              ),
              question: 'Can I use Magoosh to study for the new 2026 TOEFL?'
            },
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video or text explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: "With Magoosh Premium, you have access to 4 full-length, official practice tests, straight from the makers of the TOEFL. Because these are official tests, they are just like the real thing and reflect what you'll see on test day.",
              question: 'Do I get practice tests?'
            },
             {
              answer: "Magoosh is built entirely from official TOEFL questions and match exactly what you'll see on test day.",
              question: 'How close is the material to the actual TOEFL?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: <>
                Magoosh content is all in English, so you'll need to have some experience with English. But you don't need to be an expert. We have features that will help you prepare for the TOEFL, even if you aren't confident in your English level. We have transcripts and captions for our Lessons. You can also slowdown the speed of the lessons to make it easier to follow along. We recommend signing up for our <a href={trialOffer.registrationUrl}>free trial</a> and exploring the features to see if Magoosh is right for you.
              </>,
              question: 'What English level should I have before using Magoosh TOEFL?'
            },
            {
              answer: "Of course! Your always-available AI tutor can help unblock you on question you are struggling with. And if you still aren't getting it send us a message while you're studying, and one of our TOEFL tutors will get back to you quickly.",
              question: "If I'm struggling with a question, can I get help?"
            },
            {
              answer: "No! Many of our students are first-time TOEFL test-takers, so if you're ready to start studying now, you should go ahead and sign up! Our lessons start from the beginning and teach you everything you need to know about the TOEFL, so taking the test prior is not required.",
              question: 'Do I need to have taken the test before?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: (
                <>
                  We guarantee after completing our premium program, you'll see a <a href="/score-guarantee">4 point increase over your old score</a>, or we'll give you all of your money back.
                </>
              ),
              question: 'How does the score guarantee work?'
            },
            {
              answer: (
                <>
                  <p>If you choose to pay in four monthly installments, your first payment is due at checkout, with the remaining installments charged monthly. You'll receive an email with your payment schedule after checkout. </p><br />
                  <p><strong>Please note:</strong> Installment plans are not subscriptions—payments continue even if you pause your plan, but your access will be extended by the paused duration. By choosing an installment plan, you are committing to paying the full purchase amount unless you request a refund within 7 days of your initial purchase. Pausing your plan does not extend the refund window.</p>
                </>
              ),
              question: 'How do monthly payments work?'
            },
            {
              answer: 'We accept Visa, Mastercard, American Express, Discover, PayPal, Apple Pay, Google Pay, and Alipay',
              question: 'What types of payment do you accept?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  Yes! Check out our <a href="https://ielts.magoosh.com">IELTS website</a> to learn more.
                </>
              ),
              question: 'Does Magoosh also offer IELTS prep?'
            }
          ]}
        />

        <StartYourPrepSection
          examName="TOEFL"
          pointIncrease={4}
          primaryOffer={{ ...premium6MonthsOffer, name: '6 Months' }}
          secondaryOffer={{ ...premiumOffer, name: '1 Month' }}
          trialOffer={trialOffer}
        />
      </PageLayout>
    </div>
  );
};
